import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import fr from './locales/fr.json';
import es from './locales/es.json';

/**
 * i18n — infrastructure (Phase 1).
 *
 * The default UI language is ENGLISH (project rule). The French file is the
 * baseline for the upcoming Phase 2 translation of the ~1500 hardcoded UI
 * strings; the app must remain 100% functional in EN.
 *
 * Language is persisted in localStorage (key `bandz.locale`) by the
 * LanguageSelector component, exactly like the theme (`bandz.theme`).
 *
 * Explicitly stored preferences win; otherwise we STAY on English. We do NOT
 * auto-detect from the browser locale, because the product default is EN.
 * Phase 2 can flip on browser-language detection behind an opt-in if desired.
 */
const STORAGE_KEY = 'bandz.locale';

function resolveInitialLanguage(): string {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'fr' || saved === 'es') return saved;
  } catch {
    /* ignore storage errors */
  }
  return 'en';
}

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fr: { translation: fr },
    es: { translation: es },
  },
  lng: resolveInitialLanguage(),
  fallbackLng: 'en',
  supportedLngs: ['en', 'fr', 'es'],
  nonExplicitSupportedLngs: true,
  interpolation: {
    // React escapes output by default; react-i18next handles escaping.
    escapeValue: false,
  },
  react: {
    // Phase 1: existing hardcoded EN strings are NOT yet using t().
    // Missing keys fall back to EN gracefully.
    bindI18n: 'languageChanged',
    bindI18nStore: '',
    useSuspense: false,
  },
});

/** Persist a selected language and switch i18n immediately. */
export function setAppLanguage(lang: 'en' | 'fr' | 'es') {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
  void i18n.changeLanguage(lang);
}

// APP_LOCALE_STORAGE_KEY REMOVED (audit 2026-10-24 [DEAD]): alias of STORAGE_KEY
// with no external reader — the i18n module uses STORAGE_KEY internally.
export default i18n;